import { CheckCircle2, AlertTriangle, TrendingDown, TrendingUp, Eye, Receipt } from "lucide-react";
import { RadialBar, RadialBarChart, ResponsiveContainer, PolarAngleAxis } from "recharts";
import { fromKm, fmt, unitLabel } from "@/lib/units";

/**
 * Top-of-dashboard insight panel.
 *
 * Visual structure (matches the spec'd two-section layout):
 *   ┌──────────────────────────────────────────────────────┐
 *   │  Section A — Summary Bar                              │
 *   │  Status icon · "Smart Summary" · "You're on pace"     │
 *   │  + supporting projection message on the right         │
 *   ├──────────────────────────────────────────────────────┤
 *   │  Section B — Metric tiles (single row)                │
 *   │  Projected Year-End · Safe Daily · Pace · Deductible  │
 *   └──────────────────────────────────────────────────────┘
 *
 * Pace messaging here is driven EXCLUSIVELY by Annual Pace (projected
 * year-end mileage vs annual allowance). The Deductible tile is conditional
 * (only renders when the active range has business trips).
 *
 * The outer card uses `h-full flex flex-col` so it stretches to match the
 * sibling card's height when they sit side-by-side in a grid row.
 */
export default function SmartSummaryBanner({
  projection,
  annualPace,
  safeDailyAverage,
  daysRemainingInPolicyYear,
  yearlyLimitKm,
  unit,
  deductible = null,
}) {
  if (!projection || !annualPace) return null;

  const u = unitLabel(unit);
  const status = annualPace.status; // 'on' | 'watch' | 'over' | 'unknown'

  // Per-status visual + copy lookups — all keyed off Annual Pace.
  const statusColor =
    status === "over" ? "#FF5A5F" : status === "watch" ? "#FFB340" : "#34D17A";
  const statusRingFill =
    status === "over" ? "#E82127" : status === "watch" ? "#FFB340" : "#34D17A";
  const StatusIcon =
    status === "over" ? AlertTriangle : status === "watch" ? Eye : CheckCircle2;
  const iconWrapClass =
    status === "over"
      ? "bg-[#E82127]/15 border border-[#E82127]/30"
      : status === "watch"
      ? "bg-[#FFB340]/15 border border-[#FFB340]/30"
      : "bg-[#16A34A]/12 border border-[#16A34A]/30";

  const summaryText =
    status === "over"
      ? "You're heading over your limit"
      : status === "watch"
      ? "You're close to your limit"
      : "You're on pace";

  const deltaAbs = fmt(fromKm(Math.abs(projection.deltaKm), unit), 0);
  const summarySub =
    status === "over"
      ? `Projected to exceed your limit by ${deltaAbs} ${u}`
      : status === "watch"
      ? `Projected to finish within ${deltaAbs} ${u} of your annual cap`
      : `Projected to finish ${deltaAbs} ${u} under your limit`;

  const projectedPctCapped = Math.min(100, Math.round(annualPace.projectedPct || 0));
  const hasDeductible = !!deductible && Number.isFinite(deductible.value) && deductible.value > 0;
  // Metric tile count — drives the column count for Section B so the row
  // stays a single line at all desktop widths even at half-width.
  const tileCount = hasDeductible ? 4 : 3;
  const tilesColsClass =
    tileCount === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3";

  return (
    <div
      className="bg-[var(--bg-canvas)] border border-[var(--border-hover)] flex flex-col h-full"
      style={{ borderRadius: 4 }}
      data-testid="smart-summary-banner"
    >
      {/* ---------------- Section A — Summary Bar ---------------- */}
      <div className="px-4 lg:px-5 py-3 lg:py-4 border-b border-[var(--border-hover)] flex items-start gap-3">
        <div
          className={`mt-0.5 flex items-center justify-center w-9 h-9 shrink-0 ${iconWrapClass}`}
          style={{ borderRadius: 999 }}
        >
          <StatusIcon className="w-4 h-4" strokeWidth={1.75} style={{ color: statusColor }} />
        </div>
        <div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-center md:justify-between gap-x-4 gap-y-1">
          <div className="min-w-0">
            <div className="label-tiny mb-0.5">Smart Summary</div>
            <div
              className="font-display text-base lg:text-lg leading-tight tracking-tight"
              style={{ color: statusColor }}
              data-testid="smart-summary-status"
            >
              {summaryText}
            </div>
          </div>
          <div className="text-[11px] text-[var(--text-secondary)] leading-snug md:text-right md:max-w-[55%]">
            {summarySub}
          </div>
        </div>
      </div>

      {/* ---------------- Section B — Metric tiles ---------------- */}
      <div
        className={`grid grid-cols-2 ${tilesColsClass} divide-x divide-[var(--border-hover)] flex-1`}
      >
        {/* Projected Year-End */}
        <div className="p-4 lg:p-5">
          <div className="label-tiny mb-1.5">Projected Year-End</div>
          <div className="font-display text-2xl lg:text-3xl font-light tracking-tighter text-[var(--text-primary)]">
            {fmt(fromKm(projection.km, unit), 0)}
            <span className="text-xs lg:text-sm text-[var(--text-muted)] ml-1 font-mono-data">{u}</span>
          </div>
          <div
            className="mt-1.5 flex items-center gap-1 text-[11px] font-mono-data tracking-wider"
            style={{ color: statusColor }}
            data-testid="smart-summary-projection-delta"
          >
            {projection.overCap ? (
              <TrendingUp className="w-3.5 h-3.5" strokeWidth={2} />
            ) : (
              <TrendingDown className="w-3.5 h-3.5" strokeWidth={2} />
            )}
            {deltaAbs} {u} {projection.overCap ? "over" : "under"}
          </div>
        </div>

        {/* Safe Daily Average */}
        <div className="p-4 lg:p-5">
          <div className="label-tiny mb-1.5">Safe Daily Average</div>
          <div
            className="font-display text-2xl lg:text-3xl font-light tracking-tighter text-[var(--text-primary)]"
            data-testid="smart-summary-safe-daily"
          >
            {fmt(fromKm(safeDailyAverage || 0, unit), 0)}
            <span className="text-xs lg:text-sm text-[var(--text-muted)] ml-1 font-mono-data">{u}/day</span>
          </div>
          <div className="text-[11px] text-[var(--text-secondary)] mt-1.5 leading-snug">
            to stay within annual allowance · {daysRemainingInPolicyYear || 0} days left
          </div>
        </div>

        {/* Pace Status (Annual) */}
        <div className="p-4 lg:p-5 flex items-center gap-3 col-span-2 sm:col-span-1 border-t sm:border-t-0 border-[var(--border-hover)]">
          <div className="relative w-14 h-14 shrink-0 ring-sweep">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart
                innerRadius="70%"
                outerRadius="100%"
                data={[{ value: projectedPctCapped }]}
                startAngle={90}
                endAngle={-270}
                barSize={6}
              >
                <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                <RadialBar
                  background={{ fill: "var(--border-hover)" }}
                  dataKey="value"
                  cornerRadius={3}
                  fill={statusRingFill}
                />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center text-[11px] font-mono-data text-[var(--text-primary)]">
              {projectedPctCapped}%
            </div>
          </div>
          <div className="min-w-0">
            <div className="label-tiny mb-0.5">Pace Status</div>
            <div
              className="font-display text-sm lg:text-base font-medium tracking-tight"
              style={{ color: statusColor }}
              data-testid="smart-summary-pace-label"
            >
              {annualPace.label}
            </div>
            <div className="text-[11px] text-[var(--text-secondary)] mt-0.5 leading-snug">
              of annual allowance · projected
            </div>
          </div>
        </div>

        {/* Deductible Energy (only when business trips exist) */}
        {hasDeductible && (
          <div
            className="p-4 lg:p-5 col-span-2 sm:col-span-1 border-t sm:border-t-0 border-[var(--border-hover)]"
            data-testid="smart-summary-deductible"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <span
                className="inline-flex items-center justify-center w-5 h-5 shrink-0 bg-[#FFB340]/12 border border-[#FFB340]/30"
                style={{ borderRadius: 999 }}
              >
                <Receipt className="w-3 h-3" strokeWidth={1.75} style={{ color: "#FFB340" }} />
              </span>
              <div className="label-tiny">Est. Deductible Energy</div>
            </div>
            <div
              className="font-display text-2xl lg:text-3xl font-light tracking-tighter leading-none"
              style={{ color: "#FFB340" }}
              data-testid="smart-summary-deductible-value"
            >
              {deductible.currencySymbol}
              {deductible.value.toFixed(2)}
            </div>
            <div className="text-[11px] text-[var(--text-secondary)] mt-1.5 leading-snug">
              {deductible.businessPct.toFixed(0)}% business use · {deductible.chargeKwh.toFixed(1)} kWh ·{" "}
              {deductible.currencySymbol}
              {deductible.chargeCost.toFixed(2)} total
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
